/**
 * The one browser-wired friction observer, and the report it produces.
 *
 * Separated from `friction.ts` so that module stays pure and testable — this file is the
 * only place `localStorage` and the wall clock are touched.
 */
import { localStore } from "../localStore.ts";
import { frictionObserver, type FrictionReport } from "./friction.ts";

export const friction = frictionObserver(localStore(), () => Date.now());

/**
 * Log what the field test needs to read (#16).
 *
 * To the console rather than to a server: P0 ships no analytics endpoint, and inventing
 * one for a measurement about a login screen would be the wrong order of work. The record
 * also stays in `localStorage`, so an observer who was not watching the console at the
 * moment of sign-in can still read it off the device afterwards.
 */
export function reportFriction(): FrictionReport | null {
  const report = friction.report();
  if (report === null) return null;
  const summary =
    `signed in via ${report.method} in ${report.secondsToSignIn}s, ` +
    `${report.attempts} attempt(s), ${report.failures.length} failure(s)`;
  if (report.stalled) {
    // ADR-0008: "stalling there is a real finding, and is the evidence that would justify
    // revisiting this." Warn so it stands out in a session recording or a tethered log.
    console.warn(`[signin] STALLED — ${summary}`, report);
  } else {
    console.info(`[signin] ${summary}`, report);
  }
  return report;
}
